/**
 * Device-independent presentation of the CANONICAL play field
 * ({@link CANONICAL_VIEWPORT}). Every scene simulates/renders in canonical
 * coordinates; this maps that fixed 16:9 field onto whatever device canvas we have:
 *  - LANDSCAPE — zoom to fit + centre (letterboxed).
 *  - PORTRAIT  — ROTATE the camera 90° so the landscape field fills the tall screen,
 *    then zoom to fit. Same field, same coordinates, just oriented for the device.
 *
 * Because the MAIN camera carries this zoom + rotation, `pointer.worldX/worldY` (which
 * Phaser derives via `camera.getWorldPoint`) already lands in CANONICAL coordinates —
 * so input needs no extra maths. HUD overlays, however, must NOT inherit the camera's
 * zoom/rotation; put them in a container and call {@link alignHud} so they render
 * upright at SCREEN pixels. (`setScrollFactor(0)` only cancels SCROLL, not zoom/rotation.)
 */
import Phaser from 'phaser';
import { CANONICAL_VIEWPORT } from '../core';

/** Minimal typing for the (still partially-supported) UA Client Hints API. */
interface UADataLike {
  mobile?: boolean;
}

/** Cached mobile verdict — device class doesn't change within a session. */
let mobileCache: boolean | null = null;

/**
 * Is this a MOBILE/touch device (phone or tablet) rather than a desktop/laptop? Used
 * to decide whether to auto-orient the field to the device (mobile) or keep it
 * landscape (a desktop, even in a narrow window). Confirmed from MULTIPLE independent
 * signals so no single spoofable check decides it:
 *  1. UA Client Hints `navigator.userAgentData.mobile` (authoritative when present),
 *  2. the user-agent string (legacy phones/tablets), and
 *  3. a COARSE primary pointer with NO hover (touch-first input), and
 *  4. multi-touch capability (`maxTouchPoints > 1`).
 * A device is "mobile" if UA-CH says so, if it's an iPadOS-as-Mac (a direct override),
 * or if at least TWO of the three heuristics (UA / pointer / touch) agree — which keeps
 * touchscreen LAPTOPS (fine pointer + hover) classified as desktop.
 */
export function isMobileDevice(): boolean {
  if (mobileCache !== null) return mobileCache;
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    mobileCache = false;
    return false;
  }

  const uaData = (navigator as Navigator & { userAgentData?: UADataLike }).userAgentData;
  if (uaData && uaData.mobile === true) {
    mobileCache = true;
    return true;
  }

  const ua = navigator.userAgent || '';
  const mm = typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : null;
  const touchPoints = navigator.maxTouchPoints ?? 0;

  // iPadOS reports as desktop Safari ("Macintosh") but is multi-touch — a direct
  // override (NOT a vote, so it can't double-count the touch signal below).
  if (/Macintosh/.test(ua) && touchPoints > 1) {
    mobileCache = true;
    return true;
  }

  const uaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Silk|Tablet/i.test(ua);
  const coarseNoHover = !!mm && mm('(pointer: coarse)').matches && mm('(hover: none)').matches;
  const touch = touchPoints > 1;

  const votes = [uaMobile, coarseNoHover, touch].filter(Boolean).length;
  mobileCache = votes >= 2;
  return mobileCache;
}

/**
 * Fit + orient a `fieldW`×`fieldH` play area into the device canvas via the scene's
 * main camera. We ROTATE the landscape field 90° only on a MOBILE device held in
 * PORTRAIT (so it fills the tall screen and tracks the phone's orientation); a desktop
 * — even in a narrow/portrait window — keeps the field landscape (letterboxed), never
 * sideways. Returns true when the view was rotated.
 */
export function fitCameraToField(scene: Phaser.Scene, fieldW: number, fieldH: number): boolean {
  const cam = scene.cameras.main;
  const { width: cw, height: ch } = scene.scale.gameSize;
  // Pin the camera to the current canvas size up front, so this works regardless of
  // whether the CameraManager has processed the resize yet (centerOn uses cam.width).
  cam.setSize(cw, ch);
  const rotate = isMobileDevice() && ch > cw;
  if (rotate) {
    // Rotate the landscape field 90°: its width spans the screen's height and vice versa.
    cam.setRotation(Math.PI / 2);
    cam.setZoom(Math.min(cw / fieldH, ch / fieldW));
  } else {
    cam.setRotation(0);
    cam.setZoom(Math.min(cw / fieldW, ch / fieldH));
  }
  cam.centerOn(fieldW / 2, fieldH / 2);
  return rotate;
}

/**
 * Fit + orient the CANONICAL field (the live game's device-independent play area).
 * Call in `create()` and on every resize.
 */
export function fitCameraToCanonical(scene: Phaser.Scene): boolean {
  return fitCameraToField(scene, CANONICAL_VIEWPORT.width, CANONICAL_VIEWPORT.height);
}

/**
 * Counter the main camera's zoom + rotation on a HUD container so its children render
 * UPRIGHT at SCREEN pixels (author children in screen px from the top-left). Setting
 * the container transform to the camera's inverse means `camera ∘ container = identity`,
 * so a child placed at local (sx, sy) appears exactly at screen (sx, sy). Call after
 * {@link fitCameraToCanonical} (create + resize).
 */
export function alignHud(scene: Phaser.Scene, hud: Phaser.GameObjects.Container): void {
  const cam = scene.cameras.main;
  const { width: cw, height: ch } = scene.scale.gameSize;
  const angle = isMobileDevice() && ch > cw ? Math.PI / 2 : 0;
  const z = cam.zoom;
  // The HUD container must be the INVERSE of the camera transform so children authored
  // in screen px render at those screen px. Phaser's camera maps
  //   screen = O + R(angle)·z·(world − scroll − O),  O = camera origin (centre, px),
  // so the inverse is  pos = scroll + O − (1/z)·R(−angle)·O,  rot = −angle,  scale = 1/z.
  // Built from FRESH scalars (scroll is set synchronously by centerOn; zoom by setZoom)
  // — NOT cam.getWorldPoint, whose matrix isn't rebuilt until the render phase and is
  // therefore stale during create()/onResize().
  const ox = cw * 0.5; // camera origin default (originX/Y = 0.5)
  const oy = ch * 0.5;
  const cos = Math.cos(-angle);
  const sin = Math.sin(-angle);
  const rOx = (cos * ox - sin * oy) / z;
  const rOy = (sin * ox + cos * oy) / z;
  hud
    .setPosition(cam.scrollX + ox - rOx, cam.scrollY + oy - rOy)
    .setRotation(-angle)
    .setScale(1 / z);
}
